import { OrgUserSearchParams, OrgUserSearchRequest, UserId } from '@cibo/core'
import { DataGridPro, EmptyState, ExtendedDataGridProProps, Section } from '@cibo/ui'
import { LinearProgress, Stack, Typography } from '@mui/material'
import { GridSortModel } from '@mui/x-data-grid-pro'
import { ReactNode, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePaginatedOrgUsers } from '../../'
import { UserSearchBar } from './UserSearchBar'
import { UserSearchChips } from './UserSearchChips/UserSearchChips'
import { UserSearchFiltersProps } from './UserSearchFilters'

interface PaginatedUsersTableProps
  extends Omit<ExtendedDataGridProProps, 'rows'>,
    Pick<UserSearchFiltersProps, 'availableFilters'> {
  actions?: ReactNode | ReactNode[]
  baseQuery?: OrgUserSearchRequest
  searchValues?: OrgUserSearchParams
  onChangeSearchValues?: (searchValues: OrgUserSearchParams) => void
  goToUser(user: { userId: UserId }): void
}

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50]

export const PaginatedUsersTable = ({
  actions,
  baseQuery = {},
  goToUser,
  onChangeSearchValues,
  onPageChange,
  onPageSizeChange,
  onSortModelChange,
  pageSize = 10,
  page = 0,
  searchValues = {},
  sortModel = [],
  availableFilters,
  ...dataGridProps
}: PaginatedUsersTableProps) => {
  const { t } = useTranslation('@cibo/profile/PaginatedUsersTable')
  const [localPage, setlocalPage] = useState(page)
  const [localPageSize, setlocalPageSize] = useState(pageSize)
  const [localSortModel, setlocalSortModel] = useState<GridSortModel>(sortModel)
  const [localSearchValues, setlocalSearchValues] = useState<OrgUserSearchParams>({
    ...searchValues,
  })

  const finalPage = !!onPageChange ? page : localPage
  const finalPageSize = !!onPageSizeChange ? pageSize : localPageSize
  const finalSearchValues = !!onChangeSearchValues ? searchValues : localSearchValues
  const finalSortModel = !!onSortModelChange ? sortModel : localSortModel

  const requestQuery = useMemo(
    () => ({
      ...finalSearchValues,
      sortField: finalSortModel[0] && `${finalSortModel[0].field}:${finalSortModel[0].sort}`,
      ...baseQuery,
      limit: finalPageSize,
      offset: finalPage * finalPageSize,
    }),
    [baseQuery, finalSearchValues, finalSortModel, finalPage, finalPageSize]
  )

  const paginatedUsers = usePaginatedOrgUsers(
    {
      ...requestQuery,
    },
    { refetchOnMount: 'always' }
  )

  const rows = paginatedUsers.data ? paginatedUsers.data.items : []

  const handleSortModelChange = (newSortModel: GridSortModel, details: any) => {
    !!onSortModelChange ? onSortModelChange(newSortModel, details) : setlocalSortModel(newSortModel)
  }

  const handleSearch = (searchValues: OrgUserSearchParams) => {
    !!onChangeSearchValues ? onChangeSearchValues(searchValues) : setlocalSearchValues(searchValues)
  }

  const handlePageChange = (newPage: number, details: any) =>
    !!onPageChange ? onPageChange(newPage, details) : setlocalPage(newPage)

  const handlePageSizeChange = (newSize: number, details: any) =>
    !!onPageSizeChange ? onPageSizeChange(newSize, details) : setlocalPageSize(newSize)

  return (
    <Section sx={{ paddingInline: 1.5 }}>
      <Stack spacing={3} data-testid="users-table">
        <Stack>
          <Stack direction="row" padding={2} justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{t('users')}</Typography>
            <UserSearchBar
              availableFilters={availableFilters}
              loading={paginatedUsers.isFetching}
              onSearch={handleSearch}
              searchValues={finalSearchValues}
            />
            {!!actions ? actions : <div />}
          </Stack>
          <UserSearchChips
            searchValues={{ isActive: true, ...finalSearchValues }}
            onSearch={handleSearch}
          />
        </Stack>
        {paginatedUsers.isFetched && !rows.length ? (
          <EmptyState
            headerMarkdown={
              !!finalSearchValues.fullText
                ? t('noResultsHeaderFullTextMd', { fullText: finalSearchValues.fullText })
                : t('noResultsHeaderYourQueryMd')
            }
            helperText={
              finalSearchValues.fullText ? t('noResultsFullTextHelper') : t('noResultsQueryHelper')
            }
          />
        ) : (
          <DataGridPro
            autoHeight
            rows={rows}
            getRowId={row => row.userId}
            onRowClick={({ id }) => goToUser({ userId: id as UserId })}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            loading={paginatedUsers.isFetching}
            pageSize={finalPageSize}
            onPageSizeChange={handlePageSizeChange}
            onPageChange={handlePageChange}
            page={finalPage}
            pagination
            paginationMode="server"
            sortingMode="server"
            onSortModelChange={handleSortModelChange}
            rowCount={paginatedUsers.data?.numAvailable || 0}
            keepNonExistentRowsSelected
            sx={{ '.MuiDataGridOverlay': '300px' }}
            components={{
              LoadingOverlay: () => <LinearProgress sx={{ marginBottom: -0.5 }} />,
            }}
            {...dataGridProps}
          />
        )}
      </Stack>
    </Section>
  )
}
